import React, { useEffect, useMemo, useState, useId } from 'react';
import { FiX, FiEdit2, FiSearch } from 'react-icons/fi';
import { baseFromInclusive, inclusiveFromBase, breakupFromBase, toNum, formatINR } from '../../utils/pricing';
import hsnSacData from '../../data/hsnSacData.json';
import gstRatesData from '../../data/gstRatesData.json';
import ItemInventoryTimeline from '../inventory/ItemInventoryTimeline';

/**
 * Reusable Item Create/Edit/View Modal
 * 
 * Props:
 * - item: The item object being edited (required)
 * - onChange: (field, value) => void - callback to update item fields
 * - onClose: () => void - callback to close the modal
 * - onSave: (item) => void - callback when save button is clicked
 * - items: Array of existing items for autocomplete (optional)
 * - title: Custom title for the modal (optional)
 * - mode: 'new' | 'edit' | 'view' - modal mode (optional, defaults to item.id ? 'edit' : 'new')
 * - onModeChange: (mode) => void - callback when mode changes (e.g., view -> edit)
 * - images: Array of image URLs (optional)
 * - onAddImage: () => void - callback to trigger image picker (optional)
 * - onRemoveImage: (index) => void - callback to remove an image (optional)
 * - saving: boolean - whether save is in progress (optional)
 */
export default function EditItemModal({ 
  item, 
  onChange, 
  onClose, 
  onSave, 
  items = [], 
  categories = [],
  title,
  mode: modeProp,
  onModeChange,
  images = [],
  onAddImage,
  onRemoveImage,
  saving = false
}) {
  const uniqueId = useId();
  const controlStyle = { fontSize: 13, padding: '6px 8px', height: 34 };
  const smallBtn = { fontSize: 12, padding: '4px 10px', borderRadius: 8, height: 28, display: 'inline-flex', alignItems: 'center', gap: 6 };
  const defaultUnits = useMemo(() => ['Number', 'Hour', 'Kilogram', 'Meter', 'Litre', 'Piece', 'Ton'], []);
  const [unitOptions, setUnitOptions] = useState(() => {
    const u = (item?.unit || '').trim();
    return u && !defaultUnits.includes(u) ? [...defaultUnits, u] : defaultUnits;
  });

  // HSN/SAC search state
  const [hsnSearch, setHsnSearch] = useState('');
  const [hsnDropdownOpen, setHsnDropdownOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);

  // Determine mode
  const mode = modeProp || (item?.id ? 'edit' : 'new');
  const isView = mode === 'view';
  const [activeTab, setActiveTab] = useState('details'); // details | activity

  useEffect(() => {
    // Reset tab when item/mode changes
    setActiveTab('details');
  }, [mode, item?.id]);

  // Price mode: 'exclusive' (default) or 'inclusive'
  const [priceMode, setPriceMode] = useState('exclusive');
  // Track user's price input separately (what they see in the field)
  const [priceInput, setPriceInput] = useState(() => String(item?.price ?? ''));

  // Sync priceInput when item.price changes externally (e.g., on open or clear)
  useEffect(() => {
    if (priceMode === 'exclusive') {
      setPriceInput(String(item?.price ?? ''));
    } else {
      // Convert base to inclusive for display
      const inclusive = inclusiveFromBase(item?.price ?? 0, item);
      setPriceInput(String(inclusive));
    }
  }, [item?.id, item?.price]); // Re-sync on item change or price change

  const clamp = (val) => {
    if (val === '' || val === null || val === undefined) return '';
    const num = Number(val);
    if (Number.isNaN(num)) return '';
    return num < 0 ? 0 : num;
  };

  // Handle price mode toggle
  const handlePriceModeChange = (newMode) => {
    if (newMode === priceMode) return;
    const currentBase = toNum(item?.price);
    if (newMode === 'inclusive') {
      // Switching to inclusive: show inclusive price in input
      const inclusive = inclusiveFromBase(currentBase, item);
      setPriceInput(String(inclusive));
    } else {
      // Switching to exclusive: show base price in input
      setPriceInput(String(currentBase));
    }
    setPriceMode(newMode);
  };

  // Handle price input change
  const handlePriceChange = (val) => {
    const clamped = clamp(val);
    setPriceInput(String(clamped));
    if (priceMode === 'exclusive') {
      // Direct: input is the base price
      onChange('price', clamped);
    } else {
      // Inclusive: convert to base and store
      const base = baseFromInclusive(clamped, item);
      onChange('price', base);
    }
  };

  // Handle GST % change - derive CGST/SGST from total GST (CGST = SGST = GST/2)
  const handleGstChange = (val) => {
    const clamped = clamp(val);
    const half = clamped === '' ? '' : Number(clamped) / 2;
    onChange('igstRate', clamped);
    onChange('cgstRate', half);
    onChange('sgstRate', half);
    
    // If inclusive mode, recalculate base price
    if (priceMode === 'inclusive' && priceInput) {
      const newRates = { ...item, igstRate: clamped, cgstRate: half, sgstRate: half };
      const base = baseFromInclusive(toNum(priceInput), newRates);
      onChange('price', base);
    }
  };

  // Handle CESS change - if inclusive mode, recompute base
  const handleCessChange = (val) => {
    const clamped = clamp(val);
    onChange('cessRate', clamped);
    if (priceMode === 'inclusive' && priceInput) {
      const newRates = { ...item, cessRate: clamped };
      const base = baseFromInclusive(toNum(priceInput), newRates);
      onChange('price', base);
    }
  };

  // Handle item name change with autocomplete from existing items
  const handleNameChange = (val) => {
    onChange('name', val);
    // Check if the name matches an existing item for prefill
    if (items && items.length > 0) {
      const match = items.find(it => (it.name || '').toLowerCase() === (val || '').toLowerCase());
      if (match) {
        // Prefill fields from matched item
        onChange('description', match.description || '');
        onChange('unit', match.unit || '');
        onChange('price', match.price ?? '');
        onChange('hsnsac', match.hsnsac || '');
        onChange('cgstRate', match.cgstRate ?? '');
        onChange('sgstRate', match.sgstRate ?? '');
        onChange('igstRate', match.igstRate ?? '');
        onChange('cessRate', match.cessRate ?? '');
        onChange('category', match.category || '');
        onChange('isService', !!match.isService);
        onChange('inventory', match.inventory ?? '');
        // Update price input based on current mode
        if (priceMode === 'exclusive') {
          setPriceInput(String(match.price ?? ''));
        } else {
          const inclusive = inclusiveFromBase(match.price ?? 0, match);
          setPriceInput(String(inclusive));
        }
      }
    }
  };

  // HSN/SAC search results - search by code or description
  const hsnResults = useMemo(() => {
    if (!hsnSearch || hsnSearch.length < 2) return [];
    const q = hsnSearch.toLowerCase();
    return hsnSacData
      .filter(h => h.code.toLowerCase().includes(q) || h.desc.toLowerCase().includes(q))
      .slice(0, 50);
  }, [hsnSearch]);

  const categoryOptions = useMemo(() => {
    const fromItems = (items || [])
      .map((it) => String(it?.category || '').trim())
      .filter(Boolean);
    const fromProps = (categories || [])
      .map((cat) => String(cat || '').trim())
      .filter(Boolean);
    const current = String(item?.category || '').trim();
    const merged = [...fromProps, ...fromItems, current].filter(Boolean);
    return [...new Set(merged)].sort((a, b) => a.localeCompare(b));
  }, [categories, items, item?.category]);

  const categoryResults = useMemo(() => {
    const q = categorySearch.trim().toLowerCase();
    if (!q) return categoryOptions.slice(0, 50);
    return categoryOptions
      .filter((cat) => cat.toLowerCase().includes(q))
      .slice(0, 50);
  }, [categoryOptions, categorySearch]);

  // Look up GST rate by HSN/SAC code
  const lookupGstRate = (code) => {
    if (!code) return null;
    const cleanCode = code.replace(/[\s\-.]/g, '');
    
    // Determine if it's a service (SAC codes start with 99)
    const isService = cleanCode.startsWith('99');
    const ratesList = isService ? gstRatesData.services : gstRatesData.rates;
    
    // Try exact match first
    const exactMatch = ratesList.find(r => r.pattern === cleanCode);
    if (exactMatch) return exactMatch;
    
    // Try prefix match (longer prefix first)
    let bestMatch = null;
    let longestMatchLen = 0;
    
    for (const rate of ratesList) {
      // Check if code starts with pattern
      if (cleanCode.startsWith(rate.pattern)) {
        if (rate.pattern.length > longestMatchLen) {
          longestMatchLen = rate.pattern.length;
          bestMatch = rate;
        }
      }
      // Also check if pattern starts with code (for chapter-level matches)
      if (rate.pattern.startsWith(cleanCode) && cleanCode.length >= 2) {
        if (cleanCode.length > longestMatchLen) {
          longestMatchLen = cleanCode.length;
          bestMatch = rate;
        }
      }
    }
    
    return bestMatch;
  };

  // Handle HSN/SAC selection - also auto-populate GST rate
  const handleHsnSelect = (hsn) => {
    onChange('hsnsac', hsn.code);
    setHsnSearch('');
    setHsnDropdownOpen(false);
    
    // Auto-populate GST rate from government data
    const gstRate = lookupGstRate(hsn.code);
    if (gstRate) {
      const igst = gstRate.igst;
      const half = igst / 2;
      onChange('igstRate', igst);
      onChange('cgstRate', half);
      onChange('sgstRate', half);
      
      // If inclusive mode, recalculate base price with new rates
      if (priceMode === 'inclusive' && priceInput) {
        const newRates = { ...item, igstRate: igst, cgstRate: half, sgstRate: half };
        const base = baseFromInclusive(toNum(priceInput), newRates);
        onChange('price', base);
      }
    }
  };

  // Calculate tax breakup for display
  const breakup = useMemo(() => {
    return breakupFromBase(item?.price ?? 0, item);
  }, [item?.price, item?.cgstRate, item?.sgstRate, item?.igstRate, item?.cessRate]);

  // Check if any taxes are configured
  const hasTaxes = toNum(item?.cgstRate) > 0 || toNum(item?.sgstRate) > 0 || toNum(item?.igstRate) > 0 || toNum(item?.cessRate) > 0;

  if (!item) return null;

  const modalTitle = title || (mode === 'new' ? 'New Item' : mode === 'edit' ? 'Edit Item' : 'Item Details');
  const datalistId = `item-name-list-${uniqueId}`;
  const priceModeRadioName = `priceMode-${uniqueId}`;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', padding: 16, borderRadius: 8, width: 980, maxWidth: '98%', maxHeight: '90%', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>{modalTitle}</h3>
          <button className="btn btn-secondary" style={smallBtn} onClick={onClose}><FiX /> Close</button>
        </div>
        {isView && item?.id && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ ...smallBtn, background: activeTab === 'details' ? '#111827' : undefined, color: activeTab === 'details' ? '#fff' : undefined, borderColor: activeTab === 'details' ? '#111827' : undefined }}
              onClick={() => setActiveTab('details')}
            >
              Details
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ ...smallBtn, background: activeTab === 'activity' ? '#111827' : undefined, color: activeTab === 'activity' ? '#fff' : undefined, borderColor: activeTab === 'activity' ? '#111827' : undefined }}
              onClick={() => setActiveTab('activity')}
            >
              Activity
            </button>
          </div>
        )}
        <div className="form-section">
          {isView && activeTab === 'activity' ? (
            <ItemInventoryTimeline itemId={item?.id} />
          ) : (
          <>
          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
            <div className="form-group" style={{ flex: 2 }}>
              <label>Name</label>
              <input 
                className="form-control" 
                style={controlStyle} 
                list={!isView ? datalistId : undefined}
                value={item.name || ''} 
                onChange={(e) => handleNameChange(e.target.value)} 
                disabled={isView}
              />
              {!isView && items && items.length > 0 && (
                <datalist id={datalistId}>
                  {items.map(it => <option key={it.id} value={it.name} />)}
                </datalist>
              )}
            </div>
            <div className="form-group" style={{ flex: 1, position: 'relative' }}>
              <label>Category</label>
              {isView ? (
                <input className="form-control" style={controlStyle} value={item.category || ''} disabled />
              ) : (
                <>
                  <div style={{ position: 'relative' }}>
                    <input
                      className="form-control"
                      style={{ ...controlStyle, paddingRight: 30 }}
                      value={categoryDropdownOpen ? categorySearch : (item.category || '')}
                      onChange={(e) => {
                        setCategorySearch(e.target.value);
                        if (!categoryDropdownOpen) setCategoryDropdownOpen(true);
                        onChange('category', e.target.value);
                      }}
                      onFocus={() => {
                        setCategorySearch(item.category || '');
                        setCategoryDropdownOpen(true);
                      }}
                      onBlur={() => setTimeout(() => setCategoryDropdownOpen(false), 200)}
                      placeholder="Search or type category..."
                    />
                    <FiSearch style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
                  </div>
                  {categoryDropdownOpen && categoryResults.length > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: 6,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      maxHeight: 200,
                      overflowY: 'auto',
                      zIndex: 100
                    }}>
                      {categoryResults.map((cat, idx) => (
                        <div
                          key={`${cat}-${idx}`}
                          style={{
                            padding: '8px 10px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f3f4f6',
                            fontSize: 12
                          }}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            onChange('category', cat);
                            setCategorySearch(cat);
                            setCategoryDropdownOpen(false);
                          }}
                        >
                          {cat}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Type</label>
              <select className="form-control" style={controlStyle} value={item.isService ? 'service' : 'product'} onChange={(e) => onChange('isService', e.target.value === 'service')} disabled={isView}>
                <option value="product">Product</option>
                <option value="service">Service</option>
              </select>
            </div>
          </div>
          <div className="form-row" style={{ gap: 12 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Unit</label>
              {isView ? (
                <input className="form-control" style={controlStyle} value={item.unit || ''} disabled />
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <select className="form-control" style={controlStyle} value={unitOptions.includes(item.unit) ? (item.unit || '') : (item.unit ? 'CUSTOM' : '')} onChange={(e) => {
                    const v = e.target.value;
                    if (v === 'CUSTOM') onChange('unit', ''); else onChange('unit', v);
                  }}>
                    <option value="">Unit</option>
                    {unitOptions.map(u => <option key={u} value={u}>{u}</option>)}
                    <option value="CUSTOM">Custom…</option>
                  </select>
                  {(!unitOptions.includes(item.unit)) && (
                    <input className="form-control" style={controlStyle} placeholder="Custom unit" value={item.unit || ''} onChange={(e) => onChange('unit', e.target.value)} onBlur={(e) => {
                      const v = (e.target.value || '').trim();
                      if (v && !unitOptions.includes(v)) setUnitOptions(prev => [...prev, v]);
                    }} />
                  )}
                </div>
              )}
            </div>
            <div className="form-group" style={{ flex: 1, position: 'relative' }}>
              <label>HSN/SAC</label>
              {isView ? (
                <input className="form-control" style={controlStyle} value={item.hsnsac || ''} disabled />
              ) : (
                <>
                  <div style={{ position: 'relative' }}>
                    <input 
                      className="form-control" 
                      style={{ ...controlStyle, paddingRight: 30 }} 
                      value={hsnDropdownOpen ? hsnSearch : (item.hsnsac || '')} 
                      onChange={(e) => {
                        setHsnSearch(e.target.value);
                        if (!hsnDropdownOpen) setHsnDropdownOpen(true);
                        // Also update hsnsac if they're typing directly
                        onChange('hsnsac', e.target.value);
                      }}
                      onFocus={() => {
                        setHsnSearch(item.hsnsac || '');
                        setHsnDropdownOpen(true);
                      }}
                      onBlur={() => setTimeout(() => setHsnDropdownOpen(false), 200)}
                      placeholder="Search HSN/SAC..."
                    />
                    <FiSearch style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
                  </div>
                  {hsnDropdownOpen && hsnResults.length > 0 && (
                    <div style={{ 
                      position: 'absolute', 
                      top: '100%', 
                      left: 0, 
                      right: 0, 
                      background: '#fff', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: 6, 
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)', 
                      maxHeight: 200, 
                      overflowY: 'auto', 
                      zIndex: 100 
                    }}>
                      {hsnResults.map((h, idx) => {
                        const gstRate = lookupGstRate(h.code);
                        return (
                          <div 
                            key={`${h.code}-${idx}`}
                            style={{ 
                              padding: '8px 10px', 
                              cursor: 'pointer', 
                              borderBottom: '1px solid #f3f4f6',
                              fontSize: 12
                            }}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleHsnSelect(h)}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontWeight: 500 }}>{h.code}</span>
                              {gstRate && (
                                <span style={{ 
                                  fontSize: 10, 
                                  background: gstRate.igst === 0 ? '#d1fae5' : gstRate.igst <= 5 ? '#dbeafe' : gstRate.igst <= 12 ? '#fef3c7' : '#fee2e2',
                                  color: gstRate.igst === 0 ? '#065f46' : gstRate.igst <= 5 ? '#1e40af' : gstRate.igst <= 12 ? '#92400e' : '#991b1b',
                                  padding: '2px 6px', 
                                  borderRadius: 4,
                                  fontWeight: 600
                                }}>
                                  {gstRate.igst}% GST
                                </span>
                              )}
                            </div>
                            <div style={{ color: '#6b7280', fontSize: 11, lineHeight: 1.4 }}>{h.desc}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          {/* Description moved above taxes */}
          <div className="form-group">
            <label>Description</label>
            <textarea rows={2} className="form-control" style={{ fontSize: 13 }} value={item.description || ''} onChange={(e) => onChange('description', e.target.value)} disabled={isView} />
          </div>
          <div className="form-row" style={{ gap: 12 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label>GST %</label>
              <input type="number" step="0.01" className="form-control" style={controlStyle} value={item.igstRate ?? 0} onChange={(e) => handleGstChange(e.target.value)} disabled={isView} />
              {!isView && toNum(item.igstRate) > 0 && (
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                  CGST: {(toNum(item.igstRate) / 2).toFixed(2)}% + SGST: {(toNum(item.igstRate) / 2).toFixed(2)}%
                </div>
              )}
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>CESS %</label>
              <input type="number" step="0.01" className="form-control" style={controlStyle} value={item.cessRate ?? 0} onChange={(e) => handleCessChange(e.target.value)} disabled={isView} />
            </div>
          </div>
          <div className="form-row" style={{ gap: 12, alignItems: 'flex-start' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label>{priceMode === 'inclusive' ? 'Price (Incl. GST)' : 'Price (Excl. GST)'}</label>
              <input type="number" step="0.01" className="form-control" style={controlStyle} value={priceInput} onChange={(e) => handlePriceChange(e.target.value)} disabled={isView} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Inventory</label>
              <input type="number" className="form-control" style={controlStyle} value={item.inventory ?? 0} onChange={(e) => onChange('inventory', clamp(e.target.value))} disabled={isView || item.isService} />
            </div>
          </div>
          {/* Price mode selection - better alignment */}
          {!isView && (
            <div style={{ display: 'flex', gap: 24, fontSize: 13, marginBottom: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', margin: 0 }}>
                <input type="radio" name={priceModeRadioName} checked={priceMode === 'exclusive'} onChange={() => handlePriceModeChange('exclusive')} />
                <span>Rate is Exclusive of GST</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', margin: 0 }}>
                <input type="radio" name={priceModeRadioName} checked={priceMode === 'inclusive'} onChange={() => handlePriceModeChange('inclusive')} />
                <span>Rate is Inclusive of GST</span>
              </label>
            </div>
          )}
          {/* Price Breakup */}
          {hasTaxes && (
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, marginBottom: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6, color: '#374151' }}>Price Breakup</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '4px 12px', fontSize: 12 }}>
                <div style={{ color: '#6b7280' }}>Base Price (Excl. GST)</div>
                <div style={{ textAlign: 'right' }}>{formatINR(breakup.base)}</div>
                {toNum(item?.cgstRate) > 0 && (
                  <>
                    <div style={{ color: '#6b7280' }}>CGST ({item.cgstRate}%)</div>
                    <div style={{ textAlign: 'right' }}>{formatINR(breakup.cgstAmt)}</div>
                  </>
                )}
                {toNum(item?.sgstRate) > 0 && (
                  <>
                    <div style={{ color: '#6b7280' }}>SGST ({item.sgstRate}%)</div>
                    <div style={{ textAlign: 'right' }}>{formatINR(breakup.sgstAmt)}</div>
                  </>
                )}
                {toNum(item?.cessRate) > 0 && (
                  <>
                    <div style={{ color: '#6b7280' }}>CESS ({item.cessRate}%)</div>
                    <div style={{ textAlign: 'right' }}>{formatINR(breakup.cessAmt)}</div>
                  </>
                )}
                <div style={{ fontWeight: 600, borderTop: '1px solid #e5e7eb', paddingTop: 4, marginTop: 2 }}>Total (Incl. GST)</div>
                <div style={{ textAlign: 'right', fontWeight: 600, borderTop: '1px solid #e5e7eb', paddingTop: 4, marginTop: 2 }}>{formatINR(breakup.total)}</div>
              </div>
            </div>
          )}
          {/* Images Section */}
          {(images.length > 0 || onAddImage) && (
            <div className="form-group">
              <label>Images</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {images.map((url, idx) => (
                  <div key={`${url}-${idx}`} style={{ position: 'relative', width: 80, height: 80, border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden', background: '#f9fafb' }}>
                    <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    {!isView && onRemoveImage && (
                      <button
                        type="button"
                        onClick={() => onRemoveImage(idx)}
                        style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 4px', fontSize: 10, cursor: 'pointer' }}
                        aria-label="Remove image"
                        title="Remove"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                {!isView && onAddImage && (
                  <button type="button" className="btn btn-secondary" style={smallBtn} onClick={onAddImage}>
                    Add Image
                  </button>
                )}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            {isView ? (
              <button className="btn btn-secondary" style={smallBtn} onClick={() => onModeChange && onModeChange('edit')}><FiEdit2 /> Edit</button>
            ) : (
              <>
                <button className="btn btn-secondary" style={smallBtn} onClick={onClose}>Cancel</button>
                <button className="btn btn-primary" style={smallBtn} onClick={() => onSave && onSave(item)} disabled={saving || !item?.name}>{saving ? 'Saving…' : 'Save'}</button>
              </>
            )}
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
